import {
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Icon,
  Switch,
  Text,
} from "@prismicio/editor-ui"
import type { Editor } from "@tiptap/core"
import type { CSSProperties, MutableRefObject } from "react"

import { PassthroughTrigger } from "./PassthroughTrigger"
import {
  isFirstOrLastRow,
  isHeaderColumnToggled,
  isHeaderRowToggled,
  isSecondRow,
  isSingleRow,
} from "./utils"

interface TableRowMenuProps {
  editor: Editor
  open: boolean
  onOpenChange: (open: boolean) => void
  floatingStyle: CSSProperties
  triggerRef: (node: HTMLElement | null) => void
  targetTriggerElementRef: MutableRefObject<HTMLElement | undefined>
}

export function TableRowMenu(props: TableRowMenuProps) {
  const {
    editor,
    open,
    onOpenChange,
    floatingStyle,
    triggerRef,
    targetTriggerElementRef,
  } = props

  const hasHeaderRow = isHeaderRowToggled(editor)
  const hasSingleRow = isSingleRow(editor)

  const selection = isFirstOrLastRow(editor)

  function onDelete(event: Event) {
    const headerColumnToggled = isHeaderColumnToggled(editor)

    editor.commands.deleteRow()
    onOpenChange(false)

    // Prevent header row for a single row table
    if (hasSingleRow && hasHeaderRow) {
      toggleHeaderRow(event)

      // Remove header column if it was not previously toggled
      if (!headerColumnToggled) editor.commands.toggleHeaderColumn()
    }
  }

  function toggleHeaderRow(event: Event) {
    event.preventDefault() // prevent menu from closing
    editor.commands.toggleHeaderRow()
  }

  function onInsertAbove() {
    // Will move the header row from the current first row to the new first row
    if (hasHeaderRow && selection.isFirstRow) {
      editor.chain().toggleHeaderRow().addRowBefore().toggleHeaderRow().run()
    } else {
      editor.commands.addRowBefore()
    }

    onOpenChange(false)
  }

  function onInsertBelow() {
    editor.commands.addRowAfter()
    onOpenChange(false)
  }

  function onMoveUp() {
    const isMovingHeaderRow = hasHeaderRow && isSecondRow(editor)
    const command = editor.chain()

    if (isMovingHeaderRow) {
      command.toggleHeaderRow().moveRow("up").toggleHeaderRow()
    } else {
      command.moveRow("up")
    }

    command.run()
    onOpenChange(false)
  }

  function onMoveDown() {
    const isMovingHeaderRow = hasHeaderRow && selection.isFirstRow
    const command = editor.chain()

    if (isMovingHeaderRow) {
      command.toggleHeaderRow().moveRow("down").toggleHeaderRow()
    } else {
      command.moveRow("down")
    }

    command.run()
    onOpenChange(false)
  }

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal>
      <DropdownMenuTrigger>
        <PassthroughTrigger
          ref={triggerRef}
          targetTriggerElementRef={targetTriggerElementRef}
          style={floatingStyle}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="right" sideOffset={4}>
        <DropdownMenuLabel>
          <Text color="grey11" variant="small">
            Row options
          </Text>
        </DropdownMenuLabel>
        {selection.isFirstRow && (
          <DropdownMenuItem
            startIcon={<Icon name="viewDay" />}
            onSelect={toggleHeaderRow}
            disabled={hasSingleRow}
          >
            <Box gap={12} alignItems="center">
              <Text noWrap>Header row</Text>
              <Switch size="extraSmall" checked={hasHeaderRow} />
            </Box>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          startIcon={<Icon name="arrowUpward" />}
          onSelect={onInsertAbove}
        >
          Insert Above
        </DropdownMenuItem>
        <DropdownMenuItem
          startIcon={<Icon name="arrowDownward" />}
          onSelect={onInsertBelow}
        >
          Insert Below
        </DropdownMenuItem>
        <DropdownMenuItem
          startIcon={<Icon name="moveUp" />}
          onSelect={onMoveUp}
          disabled={selection.isFirstRow}
        >
          Move Up
        </DropdownMenuItem>
        <DropdownMenuItem
          startIcon={<Icon name="moveDown" />}
          onSelect={onMoveDown}
          disabled={selection.isLastRow}
        >
          Move Down
        </DropdownMenuItem>
        <DropdownMenuItem
          startIcon={<Icon name="delete" />}
          onSelect={onDelete}
          disabled={hasSingleRow}
        >
          Delete Row
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
